import { useState } from 'react';
import { Icon } from '../../components/Icon';
import { useAnnouncer } from '../../components/Status';
import { displayName } from '../../domain/names';
import { isAdmin } from '../../domain/permissions';
import type { PersonWithNames } from '../../domain/types';
import { useI18n } from '../../i18n';
import { useRepos } from '../../repositories';
import { useApp } from '../../app/AppState';

/**
 * A newly added relative is not hidden and not silently accepted: their circle
 * is drawn dimmed until the family recognises them. This is the line that says
 * so, and the two ways it ends —
 *
 *   • enough relatives confirming they know the person
 *     (Governance.memberApprovalsRequired, two by default), or
 *   • an administrator saying so outright, without waiting.
 *
 * Whoever added them counts as the first, so in practice one other relative is
 * usually all it takes. Nothing here appears once they are in.
 */
export function RecognitionBar({ person, className = '' }: { person: PersonWithNames; className?: string }) {
  const { t, lang } = useI18n();
  const repos = useRepos();
  const { actor, me, recognitionOf, reload } = useApp();
  const { toast } = useAnnouncer();
  const [busy, setBusy] = useState(false);
  const state = recognitionOf(person.id);
  if (state.approved || !actor) return null;
  const admin = isAdmin(actor.session.role);
  const remaining = Math.max(0, state.needed - state.count);
  const isMe = me?.id === person.id;

  const done = (ok: boolean, message: string) => {
    setBusy(false);
    if (ok) reload();
    toast(message);
  };

  const vouch = (on: boolean) => {
    setBusy(true);
    void repos.people.vouchForPerson(person.id, on).then((r) => {
      if (r.ok) done(true, r.value.approved ? t('recognise.done', { name: displayName(person, lang) }) : t('recognise.thanks'));
      else done(false, t(('messageKey' in r ? r.messageKey : 'error.unexpected') as Parameters<typeof t>[0]));
    });
  };

  const approveNow = () => {
    setBusy(true);
    void repos.people.approvePersonNow(person.id).then((r) => {
      if (r.ok) done(true, t('recognise.done', { name: displayName(person, lang) }));
      else done(false, t(('messageKey' in r ? r.messageKey : 'error.unexpected') as Parameters<typeof t>[0]));
    });
  };

  return (
    <div className={`recognise ${className}`}>
      <p className="recognise-line">
        <Icon name="clock" />
        <span>{remaining > 0 ? t('recognise.waiting', { n: remaining }) : t('recognise.waitingAny')}</span>
      </p>
      {!isMe && (
        <div className="row">
          <button type="button" className={`btn btn-sm ${state.mine ? 'btn-ghost' : 'btn-secondary'}`} disabled={busy} onClick={() => vouch(!state.mine)}>
            <Icon name={state.mine ? 'check' : 'person'} />
            <span>{state.mine ? t('recognise.mine') : t('recognise.vouch')}</span>
          </button>
          {admin && (
            <button type="button" className="btn btn-sm btn-primary is-admin-control" disabled={busy} onClick={approveNow}>
              <Icon name="check" />
              <span>{t('recognise.approveNow')}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
